/**
 * Shopify Tracking Adapter
 *
 * Handles e-commerce event tracking for Shopify integration.
 * Works with Shopify webhooks and Customer Events.
 *
 * Setup required:
 * 1. Configure Shopify webhooks in admin
 * 2. Set up webhook endpoints in your API
 * 3. Enable this adapter in tracking config
 *
 * @see https://shopify.dev/docs/apps/webhooks
 */

'use client';

import type {
  TrackingAdapter,
  TrackingEvent,
  TrackingIdentity,
} from '../types';

/**
 * Shopify tracking adapter
 *
 * Note: Most Shopify tracking happens server-side via webhooks.
 * This adapter handles client-side supplementary tracking.
 */
export class ShopifyAdapter implements TrackingAdapter {
  name = 'shopify';

  /**
   * Check if Shopify analytics is available
   */
  isAvailable(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.ShopifyAnalytics !== 'undefined'
    );
  }

  /**
   * Track an event to Shopify
   */
  async track(event: TrackingEvent): Promise<void> {
    // Most Shopify events come from webhooks (server-side)
    // Client-side tracking is mainly for supplementary data

    try {
      // Send to your API for Shopify webhook processing
      await this.sendToWebhookHandler(event);
    } catch (error) {
      console.error('Shopify tracking error:', error);
      throw error;
    }
  }

  /**
   * Identify a user in Shopify
   */
  async identify(identity: TrackingIdentity): Promise<void> {
    if (!identity.email) {
      console.warn('Shopify identify requires email');
      return;
    }

    try {
      // Send to API to create/update Shopify customer
      await fetch('/api/tracking/shopify/identify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(identity),
      });
    } catch (error) {
      console.error('Shopify identify error:', error);
      throw error;
    }
  }

  /**
   * Send event to webhook handler API
   */
  private async sendToWebhookHandler(event: TrackingEvent): Promise<void> {
    const response = await fetch('/api/tracking/shopify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(event),
    });

    if (!response.ok) {
      throw new Error(`Shopify webhook handler failed: ${response.statusText}`);
    }
  }
}

/**
 * Server-side Shopify webhook handlers
 *
 * These should be implemented in your API routes:
 *
 * @example
 * // app/api/webhooks/shopify/orders-create/route.ts
 * export async function POST(req: Request) {
 *   const order = await req.json();
 *
 *   // Verify webhook signature
 *   if (!verifyShopifyWebhook(req)) {
 *     return new Response('Unauthorized', { status: 401 });
 *   }
 *
 *   // Track to all platforms
 *   await trackEvent({
 *     eventName: 'placed_order',
 *     order: {
 *       orderId: order.id,
 *       total: order.total_price,
 *       currency: order.currency,
 *       products: order.line_items.map(item => ({
 *         productId: item.product_id,
 *         name: item.name,
 *         price: item.price,
 *         quantity: item.quantity,
 *       })),
 *     },
 *     identity: {
 *       email: order.customer.email,
 *       firstName: order.customer.first_name,
 *       lastName: order.customer.last_name,
 *     },
 *   });
 *
 *   return new Response('OK');
 * }
 */

/**
 * Shopify webhook event types to track
 */
export const SHOPIFY_WEBHOOK_TOPICS = {
  ORDERS_CREATE: 'orders/create',
  ORDERS_UPDATED: 'orders/updated',
  ORDERS_PAID: 'orders/paid',
  ORDERS_FULFILLED: 'orders/fulfilled',
  ORDERS_CANCELLED: 'orders/cancelled',
  CHECKOUTS_CREATE: 'checkouts/create',
  CHECKOUTS_UPDATE: 'checkouts/update',
  CUSTOMERS_CREATE: 'customers/create',
  CUSTOMERS_UPDATE: 'customers/update',
  CARTS_CREATE: 'carts/create',
  CARTS_UPDATE: 'carts/update',
} as const;

/**
 * Map Shopify webhook topics to tracking events
 */
export function mapShopifyWebhookToEvent(topic: string): string | null {
  const mapping: Record<string, string> = {
    'orders/create': 'placed_order',
    'orders/paid': 'placed_order',
    'checkouts/create': 'started_checkout',
    'checkouts/update': 'started_checkout',
    'customers/create': 'account_created',
    'carts/create': 'added_to_cart',
    'carts/update': 'added_to_cart',
  };

  return mapping[topic] || null;
}

// Extend window type for Shopify
declare global {
  interface Window {
    ShopifyAnalytics?: {
      lib: {
        track: (eventName: string, data?: Record<string, any>) => void;
      };
    };
  }
}
